/**
 * "Commercial Performance" workspace tab strip.
 *
 * The "money story" section — variance attribution against budget, trading
 * P&L attribution per MWh, battery commercial revenue stack.
 *
 * Tabs:
 *   Variance      → /portfolios/:id/variance
 *   Trading P&L   → /portfolios/:id/trading
 *   Battery       → /portfolios/:id/battery        (commercial view —
 *                   revenue stack, FCAS earnings)
 */

import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Tabs, Tab, Box } from '@mui/material';

const TABS = [
  { value: 'overview',       label: 'Overview',     path: 'commercial'     },
  // Variance, Trading P&L and FPP Settlement tabs are temporarily hidden —
  // the routes still resolve (resolveActiveTab still maps them) so deep
  // links keep working. FPP pulled 2026-06-10 ahead of the Friday METIS
  // demo; bring it back by re-adding
  //   { value: 'fpp', label: 'FPP', path: 'fpp' }
  { value: 'clearing-price', label: 'Spot Price & Revenues', path: 'clearing-price' },
  // The "Battery" tab has been removed — the Overview tab now adapts
  // automatically when the user selects a battery asset (FCAS / RTE /
  // cycles KPIs, revenue stack instead of production split, etc.).
  // The /battery route still resolves to BatteryCommercial for direct
  // links; it just no longer appears as a tab.
];

function resolveActiveTab(pathname) {
  if (pathname.includes('/fpp'))            return 'fpp';
  if (pathname.includes('/clearing-price')) return 'clearing-price';
  if (pathname.includes('/variance'))       return 'variance';
  if (pathname.includes('/trading'))        return 'trading';
  // /battery still routes to the standalone page (back-compat); it doesn't
  // light up any tab here since the section now folds it into Overview.
  if (pathname.includes('/commercial') || pathname.endsWith('/battery'))
    return 'overview';
  return 'overview';
}

export default function CommercialWorkspaceTabs() {
  const { portfolioId } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const active = resolveActiveTab(pathname);

  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 2 }}>
      <Tabs
        value={active}
        onChange={(_, v) => {
          const t = TABS.find((x) => x.value === v);
          if (t) navigate(`/portfolios/${portfolioId}/${t.path}`);
        }}
        variant="scrollable"
        scrollButtons="auto"
        TabIndicatorProps={{ sx: { height: 2 } }}
        sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, py: 1 } }}
      >
        {TABS.map((t) => (
          <Tab key={t.value} value={t.value} label={t.label} />
        ))}
      </Tabs>
    </Box>
  );
}
